import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { CHAINS, DEFAULT_CHAIN_ID, SUPPORTED_CHAINS } from 'consts';
import { store } from 'store';

import * as S from './ChainSwitcher.styles';

const CHAIN_COLORS: Record<number, string> = {
  [CHAINS.ethereum.id]: '#627eea',
  [CHAINS.bsc.id]: '#f0b90b',
  [CHAINS.cronos.id]: '#002D74',
  [CHAINS.base.id]: '#0052ff',
  [CHAINS.linea.id]: '#3dd0f5',
  [CHAINS.scroll.id]: '#f08c38',
  [CHAINS.manta.id]: '#4f27e9',
  [CHAINS.blast.id]: '#fcfc03',
  [CHAINS.metis.id]: '#00d2ff',
  [CHAINS.xLayer.id]: '#4b4b4b',
  [CHAINS.arbitrum.id]: '#28a0f0',
  [CHAINS.zetachain.id]: '#00815e',
  [CHAINS.polygon.id]: '#8247e5',
  [CHAINS.soneium.id]: '#aaa',
  [CHAINS.unit0.id]: '#fff',
  [CHAINS.hyper.id]: '#0f8f7f',
  [CHAINS.zeroG.id]: '#9200e1',
};

const CHAIN_NAMES: Record<number, string> = {
  [CHAINS.ethereum.id]: 'Ethereum',
  [CHAINS.bsc.id]: 'BNB Chain',
  [CHAINS.cronos.id]: 'Cronos',
  [CHAINS.base.id]: 'Base',
  [CHAINS.linea.id]: 'Linea',
  [CHAINS.scroll.id]: 'Scroll',
  [CHAINS.manta.id]: 'Manta',
  [CHAINS.blast.id]: 'Blast',
  [CHAINS.metis.id]: 'Metis',
  [CHAINS.xLayer.id]: 'X Layer',
  [CHAINS.arbitrum.id]: 'Arbitrum',
  [CHAINS.zetachain.id]: 'Zetachain',
  [CHAINS.polygon.id]: 'Polygon',
  [CHAINS.soneium.id]: 'Soneium',
  [CHAINS.unit0.id]: 'Unit Zero',
  [CHAINS.hyper.id]: 'Hyperliquid',
  [CHAINS.zeroG.id]: '0G',
};

const GALAXY_ONLY_CHAINS = [CHAINS.polygon.id];
const HIDDEN_SWAP_CHAINS = [CHAINS.polygon.id];

export const ChainSwitcher = observer(() => {
  const { chainId, setChainId } = store.web3Store;
  const { requestingChainId, mode } = store.commonStore;

  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const viewChains = SUPPORTED_CHAINS.filter((id) => {
    if (mode === 'main') return GALAXY_ONLY_CHAINS.includes(id);
    if (mode === 'swap') return !HIDDEN_SWAP_CHAINS.includes(id);
    return true;
  })
    .map((id) => ({
      id,
      name: CHAIN_NAMES[id] || `Chain ${id}`,
      color: CHAIN_COLORS[id] || '#888',
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const fallbackChainId =
    viewChains.find((chain) => chain.id === DEFAULT_CHAIN_ID)?.id ?? viewChains[0]?.id ?? DEFAULT_CHAIN_ID;
  const isStaticSwitcher = mode === 'main' || viewChains.length <= 1;

  useEffect(() => {
    if (mode !== 'swap' && mode !== 'main') return;
    if (viewChains.some((chain) => chain.id === chainId)) return;
    if (chainId === fallbackChainId) return;

    setChainId(fallbackChainId);
  }, [chainId, fallbackChainId, mode, setChainId, viewChains]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const currentChain = viewChains.find((chain) => chain.id === chainId);
  const currentName = currentChain?.name || CHAIN_NAMES[fallbackChainId] || 'Select chain';
  const currentColor = currentChain?.color || CHAIN_COLORS[fallbackChainId] || '#888';

  const handleSelect = (id: number) => {
    setChainId(id);
    setIsOpen(false);
  };

  return (
    <S.Root ref={rootRef}>
      <S.Select onClick={isStaticSwitcher ? undefined : () => setIsOpen(!isOpen)}>
        <S.ChainDot $color={currentColor} />
        {currentName}
        {!isStaticSwitcher && <S.Arrow $open={isOpen}>▾</S.Arrow>}
      </S.Select>
      {!isStaticSwitcher && isOpen && (
        <S.Dropdown>
          {viewChains.map((c) => (
            <S.ChainItem
              key={c.id}
              $isSelected={c.id === chainId}
              onClick={() => handleSelect(c.id)}
              disabled={!!requestingChainId}
            >
              <S.ChainDot $color={c.color} />
              {c.name}
            </S.ChainItem>
          ))}
        </S.Dropdown>
      )}
    </S.Root>
  );
});
